import { AppController } from 'app/controllers';
import { computed, makeObservable } from 'mobx';
import { UserPersonalInfoData } from 'src/types/user';
import * as Yup from 'yup';
import { AppViewModel } from '..';
import { UserProfile } from 'common/types/models/user';
import { NotificationTypes } from './NotificationsViewModel';
import { FlagModel } from '@zajno/common-mobx/viewModels';

export const userPersonalInfoValidationSchema: Yup.ObjectSchema<UserPersonalInfoData> = Yup.object().shape({
    email: Yup.string().email("It's not a valid email").required('Email required'),
    name: Yup.string().matches(/^[A-Za-z ]*$/, 'Please enter valid name').max(40),
});

export class UserSettingsViewModel {
    private readonly _userId: string;
    private readonly _isLoadingPeronalInfo = new FlagModel(false);
    private readonly _isLoadingReset = new FlagModel(false);

    constructor(userId: string) {
        makeObservable(this, {
            user: computed,
        });

        this._userId = userId;
    }

    get user() { return AppController.Instance.Users.repo.getUserDeferred(this._userId).current; }

    get email() { return UserProfile.getEmail(this.user); }
    get name() { return UserProfile.getName(this.user); }

    get authError() { return AppController.Instance.Auth.error; }
    get usersError() { return AppController.Instance.Users.error; }

    get isLoadingPersonalInfo() { return this._isLoadingPeronalInfo.value; }
    get isLoadingReset() { return this._isLoadingReset.value; }

    public resetPassword = async () => {
        this._isLoadingReset.setTrue();

        await AppController.Instance.Auth.resetPasswordForEmail(this.email);

        if (this.authError) {
            AppViewModel.Instance.notifications.setNotification(this.authError, NotificationTypes.error);
        } else {
            AppViewModel.Instance.notifications.setNotification('We have sent you an email with the password recovery link.');
        }

        this._isLoadingReset.setFalse();
    };

    public submitPersonalInfoForm = async ({ email, name }: UserPersonalInfoData) => {
        this._isLoadingPeronalInfo.setTrue();

        if (email && email !== this.email) {
            await AppController.Instance.Auth.updateEmail(email);

            if (this.authError) {
                AppViewModel.Instance.notifications.setNotification(this.authError, NotificationTypes.error);
                this._isLoadingPeronalInfo.setFalse();
                return;
            }
        }

        const res = await AppController.Instance.Users.updateUserPersonalName(
            this._userId,
            name || this.name,
        );

        if (this.usersError && res) {
            AppViewModel.Instance.notifications.setNotification(this.usersError, NotificationTypes.error);
        } else {
            AppViewModel.Instance.notifications.setNotification('Your personal information changed successfully.');
        }

        this._isLoadingPeronalInfo.setFalse();
    };
}
